import { useState, useEffect } from "react";
import { Icons } from "../../components/common/Icons";
import api from "../../services/api";
import { useEmployeeData } from "../../context/EmployeeContext";
import { formatDateOnly } from "../../utils/helpers";
import EmpModal from "./EmpModal";import "../../assets/styles/Employee/EmpAttendanceWidget.css";
const empMONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const empSM = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const empWD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const empDaysIn = (y, m) => new Date(y, m + 1, 0).getDate();
const empFirstDay = (y, m) => new Date(y, m, 1).getDay();
const empFmtKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

function EmpAttendanceWidget({ showActions }) {
  const { empInfo, empAttendanceData, empDailyAtt, empMonthlyStats, empInitLeaveReqs, empInitPermReqs } = useEmployeeData();

  const [tab, setTab] = useState("summary");
  const [leaveReqs, setLeaveReqs] = useState(empInitLeaveReqs);
  const [permReqs, setPermReqs] = useState(empInitPermReqs);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveDateErr, setLeaveDateErr] = useState("");
  const [leaveFromVal, setLeaveFromVal] = useState("");
  const [leaveToVal,   setLeaveToVal]   = useState("");
  const [showPermForm, setShowPermForm] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState("");
  const [suc, setSuc] = useState("");
  const [filterMonth, setFilterMonth] = useState("all");
  const [curMonth, setCurMonth] = useState(new Date().getMonth());
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [monthStats, setMonthStats] = useState(null);
  const [dailyData, setDailyData] = useState({});
  const [noticeFreeze, setNoticeFreeze] = useState(false);
  const [sandwichWarn, setSandwichWarn] = useState("");
  const [sandwichData, setSandwichData] = useState(null); // full sandwich check result
  const [sandwichConfirm, setSandwichConfirm] = useState(false);
  const [pendingLeaveData, setPendingLeaveData] = useState(null);

  // Fix 2: Fetch leave types from DB
  useEffect(() => {
    api.getLeaveTypes().then((data) => {
      if (Array.isArray(data) && data.length) {
        setLeaveTypes(data.filter((t) => t.is_active !== false && t.is_active !== 0));
      }
    }).catch(() => {});
    api.getLeaveBalance().then((data) => {
      setLeaveBalance(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  // Fix 6: Fetch month-wise stats from DB
  useEffect(() => {
    const yr = 2026;
    api.getMonthStats(curMonth + 1, yr).then((data) => {
      setMonthStats(data);
    }).catch(() => {});
    // Fix 5: Fetch daily attendance for calendar colors
    api.getDailyAttendance(curMonth + 1, yr).then((data) => {
      const map = {};
      (data || []).forEach((r) => {
        const d = r.date ? r.date.substring(0, 10) : '';
        if (d) map[d] = r.status;
      });
      setDailyData(map);
    }).catch(() => {});
  }, [curMonth]);

  // Sync when context data loads asynchronously
  useEffect(() => {if (empInitLeaveReqs?.length) setLeaveReqs(empInitLeaveReqs);}, [empInitLeaveReqs]);
  useEffect(() => {if (Array.isArray(empInitPermReqs)) setPermReqs(empInitPermReqs);}, [empInitPermReqs]);

  useEffect(() => {
    api.getEmployeeLifecycleRequests('offboarding').then((rows) => {
      const activeNotice = (Array.isArray(rows) ? rows : []).some((r) => {
        if (!r.notice_start_date || !r.notice_end_date) return false;
        const now = new Date();
        const s = new Date(r.notice_start_date);
        const e = new Date(r.notice_end_date);
        return now >= s && now <= e;
      });
      setNoticeFreeze(activeNotice);
    }).catch(() => setNoticeFreeze(false));
  }, []);

  // ── ±15 days window helpers ──
  const LEAVE_WINDOW = 15;
  const getTodayLocal = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const toInputStr = (d) => d.toISOString().split("T")[0];
  const minDate = toInputStr(addDays(getTodayLocal(), -LEAVE_WINDOW));
  const maxDate = toInputStr(addDays(getTodayLocal(),  LEAVE_WINDOW));

  const validateLeaveDates = (from, to) => {
    if (!from) return "";
    const today = getTodayLocal();
    const minD  = addDays(today, -LEAVE_WINDOW);
    const maxD  = addDays(today,  LEAVE_WINDOW);
    const fromD = new Date(from); fromD.setHours(0,0,0,0);
    const toD   = to ? new Date(to) : null; if (toD) toD.setHours(0,0,0,0);
    const fmt   = (d) => d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
    if (fromD < minD)        return `❌ Start date too early. Earliest: ${fmt(minD)} (${LEAVE_WINDOW} days back)`;
    if (fromD > maxD)        return `❌ Start date too far ahead. Latest: ${fmt(maxD)} (${LEAVE_WINDOW} days forward)`;
    if (toD && toD < fromD)  return `❌ End date cannot be before start date`;
    if (toD && toD > maxD)   return `❌ End date too far ahead. Latest: ${fmt(maxD)}`;
    return "";
  };

  // ── Client-side sandwich detection (instant, no API needed) ──
  const checkSandwich = (from, to) => {
    if (!from || !to) { setSandwichWarn(""); setSandwichData(null); return; }

    const fromD = new Date(from); fromD.setHours(0,0,0,0);
    const toD   = new Date(to);   toD.setHours(0,0,0,0);
    if (toD < fromD) { setSandwichWarn(""); setSandwichData(null); return; }

    const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const fmtDay = (d) => {
      const dd = String(d.getDate()).padStart(2,"0");
      return DAY_NAMES[d.getDay()] + " (" + DAY_NAMES[d.getDay()].slice(0,3) + ", " + dd + " " + MONTHS[d.getMonth()] + ")";
    };

    const offDaysList = [];
    let workingDays = 0;
    const cur = new Date(fromD);
    while (cur <= toD) {
      const dow = cur.getDay();
      if (dow === 0 || dow === 6) {
        offDaysList.push(fmtDay(new Date(cur)));
      } else {
        workingDays++;
      }
      cur.setDate(cur.getDate() + 1);
    }

    const totalDays = Math.round((toD - fromD) / 86400000) + 1;

    if (offDaysList.length > 0) {
      setSandwichData({ isSandwich: true, sandwichDays: offDaysList.length, workingDays, totalDays, offDaysList });
      setSandwichWarn("sandwich");
    } else {
      setSandwichData(null);
      setSandwichWarn("");
    }

    // Also fire API in background to pick up HR holidays
    api.sandwichCheck(from, to).then(data => {
      if (data && data.isSandwich && (data.sandwichDays || 0) > offDaysList.length) {
        setSandwichData(data);
        setSandwichWarn("sandwich");
      }
    }).catch(() => {});
  };

  // Submit leave directly — no confirm modal needed
  const submitLeaveAPI = async ({ selectedType, fromDate, toDate, reason }) => {
    try {
      const isSandwich = !!(sandwichData?.isSandwich);
      const sandwichDays = sandwichData?.sandwichDays || 0;
      const totalDays = sandwichData?.totalDays
        || Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / 86400000) + 1);

      const resp = await api.applyLeave({
        leaveType: selectedType, fromDate, toDate, reason,
        isSandwich, sandwichDays, days: totalDays
      });
      const effectiveType = resp?.leaveType || selectedType;
      setLeaveReqs((prev) => [...prev, {
        id: resp?.reqId || ("LR-" + String(prev.length + 1).padStart(3, "0")),
        type: effectiveType, from: fromDate, to: toDate,
        days: totalDays, sandwich: isSandwich, reason, status: "pending",
        appliedOn: new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })
      }]);
      setSuc(isSandwich
        ? `Sandwich leave submitted as Loss of Pay (${totalDays} days LOP).`
        : resp?.isLossOfPay
          ? "All leave types exhausted. Submitted as Loss of Pay."
          : "Leave request submitted to PM.");
      api.getLeaveBalance().then((data) => setLeaveBalance(Array.isArray(data) ? data : [])).catch(() => {});
      setTimeout(() => setSuc(""), 5000);
    } catch (e) { alert("Error: " + e.message); }
    setShowLeaveForm(false);
    setSelectedLeaveType("");
    setSandwichWarn(""); setSandwichData(null);
    setSandwichConfirm(false);
    setPendingLeaveData(null);
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const selectedType = String(fd.get("type") || "");
    const fromDate = String(fd.get("from") || "");
    const toDate = String(fd.get("to") || "");
    const reason = fd.get("reason") || "";
    const dateErr = validateLeaveDates(fromDate, toDate);
    if (dateErr) { alert(dateErr); return; }
    // Sandwich leave → submit directly (warning already shown inline in form)
    await submitLeaveAPI({ selectedType, fromDate, toDate, reason });
  };
  const handlePermSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.applyPermission({ date: fd.get("date"), fromTime: fd.get("from"), toTime: fd.get("to"), reason: fd.get("reason") });
      setPermReqs((prev) => [...prev, { id: "PR-" + String(prev.length + 1).padStart(3, "0"), date: fd.get("date"), time: fd.get("from") + " – " + fd.get("to"), duration: "—", reason: fd.get("reason"), status: "pending" }]);
      setSuc("Permission request submitted to PM.");
      setTimeout(() => setSuc(""), 3200);
    } catch (e) {alert("Error: " + e.message);}
    setShowPermForm(false);
  };

  const stats = monthStats || empMonthlyStats[curMonth] || { working: 20, present: 0, wfh: 0, leave: 0, holiday: 0, late: 0, half: 0 };
  const rate = stats.working > 0 ? Math.round(stats.present / stats.working * 100) : 0;

  const yr = 2026;
  const dim = empDaysIn(yr, curMonth);
  const fd = empFirstDay(yr, curMonth);
  const grid = [];
  let week = Array(fd).fill(null);
  for (let d = 1; d <= dim; d++) {week.push(d);if (week.length === 7) {grid.push(week);week = [];}}
  if (week.length) {while (week.length < 7) week.push(null);grid.push(week);}

  const getStatus = (d) => {
    if (!d) return null;
    const dow = new Date(yr, curMonth, d).getDay();
    if (dow === 0 || dow === 6) return "weekend";
    // Fix 5: Use DB attendance data for calendar colors
    const key = empFmtKey(yr, curMonth, d);
    return dailyData[key] || empDailyAtt[key] || null;
  };

  const statusColors = { present: "#2563eb", absent: "#ef4444", wfh: "#3b82f6", half: "#d97706", late: "#ca8a04", holiday: "#ea580c", weekend: "#94a3b8" };
  const statusBg = { present: "#eff6ff", absent: "#fef2f2", wfh: "#eff6ff", half: "#fffbeb", late: "#fefce8", holiday: "#fff7ed", weekend: "#f8fafc" };
  const statusLabel = { present: "Present", absent: "Absent", wfh: "WFH", half: "Half Day", late: "Late", holiday: "Holiday" };
  const leaveRemainingByType = leaveBalance.reduce((acc, row) => {
    const key = String(row.leave_type || row.type || "").trim().toLowerCase();
    if (key) acc[key] = Number(row.remaining || 0);
    return acc;
  }, {});
  const isTypeExhausted = (type) => Number(leaveRemainingByType[String(type || "").trim().toLowerCase()] ?? 0) <= 0;
  const allConfiguredLeavesExhausted = leaveTypes.length > 0 && leaveTypes.every((t) => isTypeExhausted(t.leave_type));

  useEffect(() => {
    if (!showLeaveForm) return;
    if (allConfiguredLeavesExhausted) {
      setSelectedLeaveType("Loss of Pay");
    } else if (selectedLeaveType === "Loss of Pay") {
      setSelectedLeaveType("");
    }
  }, [showLeaveForm, allConfiguredLeavesExhausted, selectedLeaveType]);

  return (
    <div>
      {suc && <div className="toast">✓ {suc}</div>}
      <div className="tab-bar">
        {[["summary", "Summary"], ["calendar", "Calendar"], ["leave", "Leave Requests"], ["permission", "Permissions"]].map(([k, l]) =>
        <button key={k} className={`tab-btn${tab === k ? " active" : ""}`} onClick={() => setTab(k)}>{l}</button>
        )}
      </div>
      <div className="actions-bar">
        {showActions && <><button className="btn-primary" onClick={() => {
            if (noticeFreeze) return alert('Leave request not applicable during notice period');
            setSelectedLeaveType("");
            setShowLeaveForm(true); setLeaveDateErr(""); setLeaveFromVal(""); setLeaveToVal("");
          }}><Icons.Plus /> Apply Leave</button>
        <button className="btn-outline" onClick={() => setShowPermForm(true)}><Icons.Clock /> Request Permission</button></>}
      </div>

      {tab === "summary" &&
      <div className="card animate-in">
          <div className="card-header"><div className="card-title">{empMONTHS[curMonth]} 2026</div>
            <select className="filter-select" value={curMonth} onChange={(e) => setCurMonth(Number(e.target.value))}>
              {empMONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div className="card-body">
            <div className="stats-grid-7">
              {[[stats.working, "Working", "#4f46e5"], [stats.present, "Present", "#2563eb"], [stats.wfh || 0, "WFH", "#3b82f6"], [stats.leave, "Leave", "#f59e0b"], [stats.holiday, "Holiday", "#ea580c"], [stats.late || 0, "Late", "#ef4444"], [stats.half || 0, "Half", "#d97706"]].map(([v, l, c], i) =>
            <div className="stat-item" key={i}>
                  <div className="stat-value empattendancewidget-dyn-1" style={{ "--empattendancewidget-1-color": c }}>{v}</div>
                  <div className="stat-label">{l}</div>
                </div>
            )}
            </div>
            <div className="progress-bar-wrap">
              <div className="progress-label"><span>Attendance Rate</span><span style={{ "--empattendancewidget-2-color": rate >= 85 ? "#2563eb" : "#f59e0b" }} className="empattendancewidget-dyn-2">{rate}%</span></div>
              <div className="progress-track"><div className="progress-fill empattendancewidget-dyn-3" style={{ "--empattendancewidget-3-width": `${rate}%`, "--empattendancewidget-3-background": rate >= 85 ? "linear-gradient(90deg,#2563eb,#60a5fa)" : "linear-gradient(90deg,#f59e0b,#fbbf24)" }} /></div>
            </div>
          </div>
        </div>
      }

      {tab === "calendar" &&
      <div className="card animate-in">
          <div className="card-body">
            <div className="empattendancewidget-inline-1">
              <div className="empattendancewidget-inline-2">{empMONTHS[curMonth]} 2026</div>
            </div>
            <div className="legend-bar">
              {Object.entries(statusLabel).map(([k, v]) =>
            <div className="legend-item" key={k}><div className="legend-dot empattendancewidget-dyn-4" style={{ "--empattendancewidget-4-background": statusColors[k] }} />{v}</div>
            )}
            </div>
            <table className="cal-table">
              <thead><tr>{empWD.map((d) => <th key={d}>{d}</th>)}</tr></thead>
              <tbody>
                {grid.map((w, wi) =>
              <tr key={wi}>{w.map((d, di) => {
                  const s = getStatus(d);
                  return (
                    <td key={di}>
                        {d ? <div className={`${`cal-day${s ? " " + s : ""}`} empattendancewidget-dyn-5`} style={{ "--empattendancewidget-5-background": s ? statusBg[s] : "transparent", "--empattendancewidget-5-color": s ? statusColors[s] : "#334155" }}>{d}</div> : null}
                      </td>);

                })}</tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      }

      {tab === "leave" &&
      <div className="card animate-in">
          <div className="card-body empattendancewidget-inline-3">
            <table className="data-table">
              <thead><tr><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>PM</th><th>HR</th><th>Status</th><th>Remarks</th></tr></thead>
              <tbody>{leaveReqs.map((r) =>
              <tr key={r.id}>
                  <td className="empattendancewidget-inline-4">{r.type}</td><td>{formatDateOnly(r.from)}</td><td>{formatDateOnly(r.to)}</td><td>{r.days}</td>
                  <td className="empattendancewidget-inline-5">{r.reason}</td>
                  <td>{r.pmStatus || r.pm_status || "-"}</td>
                  <td>{r.hrStatus || r.hr_status || "-"}</td>
                  <td><span className={`status-badge ${r.status}`}><span className="sb-dot" />{r.status === 'manager_approved' ? 'PM Approved' : r.status}</span></td>
                  <td>{r.pmComment || r.pm_comment || r.hrComment || r.hr_comment || "-"}</td>
                </tr>
              )}</tbody>
            </table>
          </div>
        </div>
      }

      {tab === "permission" &&
      <div className="card animate-in">
          <div className="card-body empattendancewidget-inline-6">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Time</th><th>Duration</th><th>Reason</th><th>Status</th></tr></thead>
              <tbody>{permReqs.map((r) =>
              <tr key={r.id}>
                  <td className="empattendancewidget-inline-7">{formatDateOnly(r.date)}</td>
                  <td>{r.time || `${r.fromTime || r.from_time || '-'} - ${r.toTime || r.to_time || '-'}`}</td>
                  <td>{r.duration || `${r.hours || 0}h`}</td>
                  <td className="empattendancewidget-inline-8">{r.reason}</td>
                  <td><span className={`status-badge ${r.status}`}><span className="sb-dot" />{r.status === 'manager_approved' ? 'PM Approved' : r.status}</span></td>
                </tr>
              )}</tbody>
            </table>
          </div>
        </div>
      }

      {showLeaveForm && <EmpModal title="Apply for Leave" onClose={() => setShowLeaveForm(false)}>
        <form onSubmit={handleLeaveSubmit}>
          <div className="form-group"><label className="form-label">Leave Type</label><select name="type" className="form-input" required value={selectedLeaveType} onChange={(e) => setSelectedLeaveType(e.target.value)}><option value="">Select</option>{leaveTypes.length ? leaveTypes.map((t) => <option key={t.leave_type} value={t.leave_type} disabled={isTypeExhausted(t.leave_type)}>{t.leave_type}{isTypeExhausted(t.leave_type) ? " (Exhausted)" : ""}</option>) : <><option value="Casual Leave">Casual Leave</option><option value="Sick Leave">Sick Leave</option><option value="Earned Leave">Earned Leave</option><option value="Personal Leave">Personal Leave</option><option value="Comp Off">Comp Off</option><option value="WFH">Work From Home</option></>}<option value="Loss of Pay" disabled={!allConfiguredLeavesExhausted}>Loss of Pay{!allConfiguredLeavesExhausted ? " (Auto only)" : ""}</option></select></div>
          {allConfiguredLeavesExhausted && <div className="form-group" style={{ color: "#dc2626", fontSize: "12px" }}>All leave types are exhausted. New requests will be processed as Loss of Pay.</div>}
          {/* ±15 days window notice */}
          <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"8px 12px",
            marginBottom:10,fontSize:11,color:"#1e40af",lineHeight:1.6}}>
            📅 <b>Leave window:</b> You can apply for leave within <b>±{LEAVE_WINDOW} days</b> from today.<br/>
            Allowed range: <b>{new Date(minDate).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</b>
            {" → "}
            <b>{new Date(maxDate).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</b>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">From</label>
              <input name="from" type="date" className="form-input" required
                min={minDate} max={maxDate}
                value={leaveFromVal}
                onChange={e => {
                  setLeaveFromVal(e.target.value);
                  setLeaveDateErr(validateLeaveDates(e.target.value, leaveToVal));
                  checkSandwich(e.target.value, leaveToVal);
                }} />
            </div>
            <div className="form-group">
              <label className="form-label">To</label>
              <input name="to" type="date" className="form-input" required
                min={leaveFromVal || minDate} max={maxDate}
                value={leaveToVal}
                onChange={e => {
                  setLeaveToVal(e.target.value);
                  setLeaveDateErr(validateLeaveDates(leaveFromVal, e.target.value));
                  checkSandwich(leaveFromVal, e.target.value);
                }} />
            </div>
          </div>
          {leaveDateErr && (
            <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:7,
              padding:"7px 12px",fontSize:12,color:"#dc2626",marginBottom:8,fontWeight:600}}>
              {leaveDateErr}
            </div>
          )}
          {sandwichWarn && sandwichData && (
            <div style={{
              background:"#fef2f2", border:"2px solid #dc2626", borderRadius:10,
              padding:"12px 14px", marginBottom:10
            }}>
              {/* Header */}
              <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10}}>
                <span style={{fontSize:18}}>🚨</span>
                <div>
                  <div style={{fontWeight:800, fontSize:13, color:"#dc2626"}}>Sandwich Leave Detected</div>
                  <div style={{fontSize:11, color:"#7f1d1d", marginTop:1}}>
                    This leave spans a weekend/holiday — <strong>entire leave will be Loss of Pay (LOP)</strong>
                  </div>
                </div>
              </div>
              {/* Stats row */}
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:10}}>
                <div style={{background:"#fff", borderRadius:7, padding:"8px 10px", border:"1px solid #fecaca", textAlign:"center"}}>
                  <div style={{fontSize:22, fontWeight:800, color:"#dc2626"}}>{sandwichData.totalDays}</div>
                  <div style={{fontSize:9, color:"#dc2626", fontWeight:700, textTransform:"uppercase", marginTop:1}}>Total Days</div>
                  <div style={{fontSize:9, color:"#b91c1c", marginTop:1}}>ALL as LOP</div>
                </div>
                <div style={{background:"#fff", borderRadius:7, padding:"8px 10px", border:"1px solid #fecaca", textAlign:"center"}}>
                  <div style={{fontSize:22, fontWeight:800, color:"#dc2626"}}>{sandwichData.sandwichDays}</div>
                  <div style={{fontSize:9, color:"#dc2626", fontWeight:700, textTransform:"uppercase", marginTop:1}}>Off-Days</div>
                  <div style={{fontSize:9, color:"#b91c1c", marginTop:1}}>Sat / Sun / Holiday</div>
                </div>
              </div>
              {/* Off days pills */}
              {(sandwichData.offDaysList||[]).length > 0 && (
                <div style={{display:"flex", flexWrap:"wrap", gap:4, marginBottom:8}}>
                  {sandwichData.offDaysList.slice(0,5).map((d,i) => (
                    <span key={i} style={{background:"#fee2e2", color:"#b91c1c", fontSize:10,
                      fontWeight:700, padding:"2px 7px", borderRadius:99, border:"1px solid #fecaca"}}>
                      {d}
                    </span>
                  ))}
                  {sandwichData.offDaysList.length > 5 &&
                    <span style={{fontSize:10, color:"#9ca3af", alignSelf:"center"}}>+{sandwichData.offDaysList.length-5} more</span>}
                </div>
              )}
              {/* Policy note */}
              <div style={{background:"#fff7ed", border:"1px solid #fbbf24", borderRadius:6,
                padding:"7px 10px", fontSize:11, color:"#92400e", lineHeight:1.6}}>
                <strong>Company Policy:</strong> When leave is applied sandwiching a weekend or holiday,
                the <em>entire leave period</em> is treated as Loss of Pay.
                No leave balance will be deducted — this will appear in your LOP record.
              </div>
            </div>
          )}
          <div className="form-group"><label className="form-label">Reason</label><textarea name="reason" className="form-input" placeholder="Reason..." required /></div>
          <div className="form-actions">
            <button type="button" className="btn-outline" onClick={() => { setShowLeaveForm(false); setSelectedLeaveType(""); setLeaveDateErr(""); setLeaveFromVal(""); setLeaveToVal(""); setSandwichWarn(""); setSandwichData(null); }}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={!!leaveDateErr} style={{opacity:leaveDateErr?0.5:1, background: sandwichWarn ? "#dc2626" : undefined}}>
              {sandwichWarn ? "Submit as LOP" : "Submit"}
            </button>
          </div>
        </form>
      </EmpModal>}

      {showPermForm && <EmpModal title="Request Permission" onClose={() => setShowPermForm(false)}>
        <form onSubmit={handlePermSubmit}>
          <div className="form-group"><label className="form-label">Date</label><input name="date" type="date" className="form-input" required /></div>
          <div className="form-row"><div className="form-group"><label className="form-label">From</label><input name="from" type="time" className="form-input" required /></div><div className="form-group"><label className="form-label">To</label><input name="to" type="time" className="form-input" required /></div></div>
          <div className="form-group"><label className="form-label">Reason</label><textarea name="reason" className="form-input" placeholder="Reason..." required /></div>
          <div className="form-actions"><button type="button" className="btn-outline" onClick={() => setShowPermForm(false)}>Cancel</button><button type="submit" className="btn-submit">Submit</button></div>
        </form>
      </EmpModal>}
    </div>);

}

export default EmpAttendanceWidget;
